import confetti from 'canvas-confetti';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { pickRandomSeat, teamOfSeat, type Seat } from '../game/match';
import { SpinHub, Wheel, type WheelHandle } from '../components/Wheel';
import { Avatar, Name, Spinner, teamAvatarFill } from '../components/ui';
import { navigate } from '../lib/router';
import { isMuted, setMuted, subscribeMuted, unlockAudio } from '../lib/sound';
import { spin, toMatchState } from '../lib/store';
import { announce, dealingPhrases } from '../lib/voice';
import { BackButton, NotFound, ScreenHeader, SyncBadge, useMatchData } from './shared';

export function WheelScreen({ code }: { code: string }) {
  const { data, error, canEdit } = useMatchData(code);
  const wheel = useRef<WheelHandle>(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<Seat | null>(null);
  const muted = useSyncExternalStore(subscribeMuted, isMuted);
  const firstDealer = data?.match.firstDealerSeat ?? null;
  const locked = (data?.deals.length ?? 0) > 0;

  // Show an existing result (reload, or someone else spun) without animating.
  useEffect(() => {
    if (!spinning && firstDealer !== null) {
      wheel.current?.showSeat(firstDealer);
      setResult(firstDealer);
    }
  }, [firstDealer, spinning, data !== undefined]);

  if (error && !data) return <NotFound message={error} />;
  if (!data) return <Spinner label="Loading match" />;
  const { match } = data;

  const doSpin = async () => {
    if (spinning || !canEdit || locked) return;
    unlockAudio();
    // Fair result FIRST (crypto.getRandomValues), then animate to it.
    const seat = pickRandomSeat();
    setSpinning(true);
    setResult(null);
    await wheel.current?.spinTo(seat);
    spin(code, seat);
    setResult(seat);
    setSpinning(false);
    const team = match.teams[teamOfSeat(seat)];
    void confetti({
      particleCount: 120,
      spread: 75,
      origin: { y: 0.35 },
      colors: [team.color, '#d4a84b', '#f7f1e3'],
      disableForReducedMotion: true,
    });
  };

  const winner = result !== null ? match.seats[result]! : null;
  return (
    <main className="mx-auto max-w-lg px-4 pb-16">
      <ScreenHeader
        step="3 of 3"
        title="Who deals first?"
        left={<BackButton href="/home" label="Home" />}
        right={<SyncBadge code={code} />}
      />

      <div
        className="relative rounded-full focus-visible:outline-offset-8"
        tabIndex={canEdit && !locked ? 0 : -1}
        role={canEdit && !locked ? 'button' : 'img'}
        aria-label={
          canEdit && !locked
            ? `Dealer wheel with ${match.seats.map((s) => s.name).join(', ')}. Press Enter or Space to spin.`
            : `Dealer wheel with ${match.seats.map((s) => s.name).join(', ')}`
        }
        aria-disabled={spinning || undefined}
        onClick={doSpin}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void doSpin();
          }
        }}
      >
        <Wheel ref={wheel} seats={match.seats} teams={match.teams} center={canEdit && !locked && !spinning && !winner ? <SpinHub onSpin={() => void doSpin()} /> : undefined} />
      </div>

      <div className="mt-6 min-h-40" aria-live="assertive">
        {spinning ? (
          <p className="text-center font-display text-2xl font-bold text-gold-soft">Spinning…</p>
        ) : winner ? (
          <div className="card animate-rise flex flex-col items-center gap-3 p-5 text-center">
            <Avatar name={winner.name} avatarKey={winner.avatarKey} size={72} ring={match.teams[teamOfSeat(result!)].color} fill={teamAvatarFill(match.teams[teamOfSeat(result!)].color)} />
            <p className="font-display text-3xl font-extrabold">
              <Name>{winner.name}</Name> deals first!
            </p>
            {match.firstDealerSpunAt && (
              <p className="text-xs text-ink-soft">Spun {new Date(match.firstDealerSpunAt).toLocaleString()}</p>
            )}
            <div className="mt-1 flex w-full gap-2">
              {canEdit && !locked && (
                <button type="button" className="btn btn-outline-ink flex-1" onClick={doSpin}>
                  ↻ Re-spin
                </button>
              )}
              <button
                type="button"
                className="btn btn-ink flex-1"
                onClick={() => {
                  announce(dealingPhrases(toMatchState(data), match.seats));
                  navigate(`/m/${code}`);
                }}
              >
                {canEdit && !locked ? 'Confirm ✓' : 'Go to match'}
              </button>
            </div>
          </div>
        ) : canEdit ? (
          <p className="text-center text-sm font-semibold text-cream/75">Tap SPIN in the middle of the wheel.</p>
        ) : (
          <p className="panel p-4 text-center text-cream/80">Waiting for the host to spin the wheel.</p>
        )}
      </div>

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          className="btn btn-ghost min-h-10 px-3 text-sm"
          aria-pressed={muted}
          onClick={() => setMuted(!muted)}
        >
          {muted ? '🔇 Sound off' : '🔊 Sound on'}
        </button>
      </div>
    </main>
  );
}
